import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { UnidadeConsumidora, AnaliseCadastral, Negociacao, ReconciliacaoResultado } from '../../../../types';
import { Zap, UploadCloud, AlertCircle, XSquare, Activity, X, Loader2, AlertTriangle, FileWarning, Info, FileImage, Trash2, ExternalLink, Calculator, ChevronDown, TrendingUp, TrendingDown } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { useToast } from '../../../../src/contexts/ToastContext';
import { extrairFatura } from '../../../../src/services/extracaoApi';
import { mapEstruturadoParaAnaliseTecnica, type SugestaoAnaliseTecnica } from './faturaToAnaliseTecnica';
import SugestaoFaturaPanel, { type AplicacaoSugestao } from './SugestaoFaturaPanel';
import { calcularConsumoUc } from '../../../../src/lib/analiseTecnicaConsumo';
import RegraCalculoConsumo from '../../../../src/components/RegraCalculoConsumo';

/** Convert BR date dd/mm/aaaa to ISO yyyy-mm-dd */
const brDateToISO = (brDate: string): string => {
    const cleaned = brDate.replace(/[^\d/]/g, '');
    const parts = cleaned.split('/');
    if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return '';
};

/** Convert ISO yyyy-mm-dd to BR dd/mm/aaaa */
const isoToBrDate = (iso: string): string => {
    if (!iso) return '';
    // Already BR format?
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(iso)) return iso;
    const parts = iso.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return iso;
};

/** Auto-format as user types dd/mm/aaaa */
const formatDateInput = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

/** Handle paste of BR date into a text field — returns formatted BR date or '' */
const handleDatePaste = (e: React.ClipboardEvent<HTMLInputElement>): string => {
    const pasted = e.clipboardData.getData('text').trim();
    // Try dd/mm/aaaa
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(pasted)) {
        e.preventDefault();
        return pasted;
    }
    // Try yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(pasted)) {
        e.preventDefault();
        return isoToBrDate(pasted);
    }
    return '';
};

interface UcAnalysisModalProps {
    isOpen: boolean;
    onClose: () => void;
    uc: UnidadeConsumidora;
    onSave: (id: number, updates: Partial<UnidadeConsumidora>) => void;
    analiseData?: Partial<AnaliseCadastral>;
    onAnaliseChange?: (data: Partial<AnaliseCadastral>) => void;
    negociacao?: Negociacao;
}

const BooleanToggle = ({ label, value, onChange, alertWhen }: { label: string, value: boolean | undefined, onChange: (val: boolean) => void, alertWhen?: 'true' | 'false' }) => {
    const isAlert = alertWhen !== undefined && value === (alertWhen === 'true');
    return (
        <div className={`flex flex-col gap-2 p-3.5 rounded-xl border-2 shadow-sm transition-all ${isAlert ? 'border-red-400 dark:border-red-500/70 bg-red-50/50 dark:bg-red-900/15 ring-1 ring-red-300/50 dark:ring-red-500/30' : 'border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-900/60 hover:border-slate-300 dark:hover:border-slate-600'}`}>
            <div className="flex items-center gap-2">
                {isAlert && <AlertTriangle size={14} className="text-red-500 shrink-0 animate-pulse" />}
                <span className={`text-sm font-bold ${isAlert ? 'text-red-700 dark:text-red-400' : 'text-slate-700 dark:text-slate-200'}`}>{label}</span>
            </div>
            <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg w-full">
                <button
                    type="button"
                    className={`flex-1 py-1.5 px-3 text-xs font-bold rounded-md transition-all ${value === true ? (alertWhen === 'true' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 shadow-sm border border-red-200 dark:border-red-800' : 'bg-white dark:bg-slate-700 text-primary shadow-sm border border-slate-200/50 dark:border-slate-600') : 'text-slate-500 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 border border-transparent'}`}
                    onClick={() => onChange(true)}
                >
                    Sim
                </button>
                <button
                    type="button"
                    className={`flex-1 py-1.5 px-3 text-xs font-bold rounded-md transition-all ${value === false ? (alertWhen === 'false' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 shadow-sm border border-red-200 dark:border-red-800' : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 shadow-sm border border-slate-200/50 dark:border-slate-600') : 'text-slate-500 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 border border-transparent'}`}
                    onClick={() => onChange(false)}
                >
                    Não
                </button>
            </div>
            {isAlert && <span className="text-[10px] font-bold text-red-500 dark:text-red-400 mt-0.5">⚠ Condição gera pendência documental</span>}
        </div>
    );
};

export default function UcAnalysisModal({ isOpen, onClose, uc, onSave, analiseData, onAnaliseChange, negociacao }: UcAnalysisModalProps) {
    const { addToast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const backdropMouseDownRef = useRef(false);
    const [uploading, setUploading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<{ name: string; url: string; type: string } | null>(null);
    // Leitura do robô determinístico aguardando revisão do analista. Fica FORA de
    // activeUc de propósito: só entra no formulário quando ele clica em "Aplicar".
    const [extraindo, setExtraindo] = useState(false);
    const [sugestao, setSugestao] = useState<{ dados: SugestaoAnaliseTecnica; reconciliacao: ReconciliacaoResultado | null } | null>(null);

    const defaultHistoryTable = Array.from({ length: 12 }).map(() => ({
        month: '',
        consumo: 0,
        injecao: 0,
        vencimento: '',
        pagamento: '',
        emAberto: false,
        delayDays: 0,
        ocorrencia: '',
        semRegistro: false
    }));

    const [dragging, setDragging] = useState(false);
    // Explicação do cálculo da média fica recolhida por padrão: quem confere abre.
    const [regraAberta, setRegraAberta] = useState(false);
    const [activeUc, setActiveUc] = useState({
        titularidade: '',
        nParceiro: '',
        unidadeConsumidora: '',
        novoNumeroUc: '',
        grupo: '',
        tipoFornecimento: '',
        dataLeitura: '',
        dataApresentacao: '',
        dataVencimento: '',
        dataProcessamento: '',
        dataNascimentoCliente: '',
        checks: {
            atualizada: undefined as boolean | undefined,
            grupoB: undefined as boolean | undefined,
            explicacaoGrupoB: '',
            beneficio: undefined as boolean | undefined,
            consumo500: undefined as boolean | undefined,
            explicacaoConsumo500: '',
            possuiGD: undefined as boolean | undefined,
            explicacaoGD: '',
            injetando: undefined as boolean | undefined,
            mesmoTitular: undefined as boolean | undefined,
            distratoRecebido: undefined as boolean | undefined,
        },
        historyTable: defaultHistoryTable
    });

    useEffect(() => {
        if (isOpen && uc) {
            const dte = uc.dados_tecnicos_equatorial as any;
            setActiveUc({
                titularidade: uc.titular_fatura || dte?.titularidade || uc.nome_cliente || '',
                nParceiro: uc.num_parceiro || dte?.nParceiro || '',
                unidadeConsumidora: uc.numero_uc || '',
                novoNumeroUc: dte?.novoNumeroUc || '',
                grupo: uc.grupo_tarifario || dte?.grupo || 'B1',
                tipoFornecimento: uc.tipo_ligacao || dte?.tipoFornecimento || 'Monofásico',
                dataLeitura: dte?.dataLeitura || '',
                dataApresentacao: dte?.dataApresentacao || '',
                dataVencimento: dte?.dataVencimento || '',
                dataProcessamento: dte?.dataProcessamento || '',
                dataNascimentoCliente: uc.data_nascimento_cliente || '',
                checks: {
                    atualizada: dte?.checks?.atualizada,
                    grupoB: dte?.checks?.grupoB,
                    explicacaoGrupoB: dte?.checks?.explicacaoGrupoB || '',
                    beneficio: dte?.checks?.beneficio,
                    consumo500: dte?.checks?.consumo500,
                    explicacaoConsumo500: dte?.checks?.explicacaoConsumo500 || '',
                    possuiGD: dte?.checks?.possuiGD,
                    explicacaoGD: dte?.checks?.explicacaoGD || '',
                    injetando: dte?.checks?.injetando,
                    mesmoTitular: dte?.checks?.mesmoTitular,
                    distratoRecebido: dte?.checks?.distratoRecebido,
                },
                historyTable: dte?.historyTable && dte.historyTable.length > 0
                    ? dte.historyTable.map((item: any) => ({
                        ...item,
                        // Strip old auto-generated month names (e.g. "jan/2026") — only keep mm/aa format
                        month: /^\d{2}\/\d{2}$/.test(item.month || '') ? item.month : '',
                        consumo: typeof item.consumo === 'string' && item.consumo.endsWith('.00') ? item.consumo.slice(0, -3) : item.consumo,
                        injecao: typeof item.injecao === 'string' && item.injecao.endsWith('.00') ? item.injecao.slice(0, -3) : (item.injecao ?? 0),
                        emAberto: item.emAberto ?? false,
                        semRegistro: item.semRegistro ?? false
                    }))
                    : defaultHistoryTable
            });
            // Sugestão é sempre da sessão atual de upload — não sobrevive à troca de UC.
            setSugestao(null);
            // Restore uploaded file from persisted data
            if (dte?.faturaUrl) {
                setUploadedFile({ name: dte.faturaNome || 'Fatura', url: dte.faturaUrl, type: dte.faturaType || 'application/pdf' });
            } else {
                setUploadedFile(null);
            }
        }
    }, [isOpen, uc]);

    const handleUcFieldChange = (field: string, value: string) => {
        setActiveUc(prev => ({ ...prev, [field]: value }));
    };

    const handleUcCheckChange = (key: string, value: boolean | string | undefined) => {
        setActiveUc(prev => ({
            ...prev,
            checks: { ...prev.checks, [key]: value }
        }));
    };

    /** Resolve a date string that might be BR (dd/mm/aaaa) or ISO (yyyy-mm-dd) */
    const resolveDate = (d: string): Date | null => {
        if (!d) return null;
        // BR format
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) {
            const iso = brDateToISO(d);
            return iso ? new Date(iso) : null;
        }
        return new Date(d);
    };

    const calculateDelay = (vencimento: string, pagamento: string, emAberto?: boolean) => {
        if (!vencimento) return 0;
        const vDate = resolveDate(vencimento);
        if (!vDate || isNaN(vDate.getTime())) return 0;
        if (emAberto) {
            const today = new Date();
            const diffDays = Math.ceil((today.getTime() - vDate.getTime()) / (1000 * 60 * 60 * 24));
            return diffDays > 0 ? diffDays : 0;
        }
        if (!pagamento) return 0;
        const pDate = resolveDate(pagamento);
        if (!pDate || isNaN(pDate.getTime())) return 0;
        const diffDays = Math.ceil((pDate.getTime() - vDate.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const handleHistoryChange = (index: number, field: string, value: string | boolean) => {
        setActiveUc(prev => {
            const newHistory = [...prev.historyTable];
            newHistory[index] = { ...newHistory[index], [field]: value };

            if (field === 'emAberto' && value === true) {
                newHistory[index].pagamento = '';
            }
            if (field === 'semRegistro' && value === true) {
                newHistory[index].consumo = 0;
                newHistory[index].injecao = 0;
                newHistory[index].vencimento = '';
                newHistory[index].pagamento = '';
                newHistory[index].emAberto = false;
                newHistory[index].delayDays = 0;
                newHistory[index].ocorrencia = '';
            }

            if (field === 'vencimento' || field === 'pagamento' || field === 'emAberto') {
                newHistory[index].delayDays = calculateDelay(newHistory[index].vencimento, newHistory[index].pagamento, newHistory[index].emAberto);
            }

            return { ...prev, historyTable: newHistory };
        });
    };

    const getActiveUcCalc = () => {
        const history = activeUc.historyTable;
        let isComplete = true;
        let missingFields: string[] = [];

        if (!activeUc.titularidade) { isComplete = false; missingFields.push('Titularidade'); }

        if (!activeUc.unidadeConsumidora) { isComplete = false; missingFields.push('UC'); }
        if (!activeUc.grupo) { isComplete = false; missingFields.push('Grupo Tarifário'); }
        if (!activeUc.tipoFornecimento) { isComplete = false; missingFields.push('Tipo de Ligação'); }
        if (!activeUc.dataLeitura) { isComplete = false; missingFields.push('Data da Leitura'); }
        if (!activeUc.dataApresentacao) { isComplete = false; missingFields.push('Data Apresentação'); }
        if (!activeUc.dataVencimento) { isComplete = false; missingFields.push('Data Vencimento'); }
        if (!activeUc.dataProcessamento) { isComplete = false; missingFields.push('Data Processamento'); }

        const requiredChecks = ['atualizada', 'grupoB', 'beneficio', 'consumo500', 'possuiGD', 'injetando', 'mesmoTitular'];
        requiredChecks.forEach(chk => {
            if (activeUc.checks[chk as keyof typeof activeUc.checks] === undefined) {
                isComplete = false;
                if (!missingFields.includes('Checklist Sim/Não')) missingFields.push('Checklist Sim/Não');
            }
        });

        history.forEach((r: any) => {
            if (!r.semRegistro) {
                if (r.consumo === null || r.consumo === undefined || r.consumo.toString() === '') isComplete = false;
                if (!r.vencimento || (!r.pagamento && !r.emAberto && r.ocorrencia === '')) isComplete = false;
            }
        });

        if (!isComplete && !missingFields.includes('Histórico')) missingFields.push('Histórico Incompleto');

        if (activeUc.checks.grupoB === false && !activeUc.checks.explicacaoGrupoB) {
            isComplete = false; missingFields.push('Justificativa Grupo B');
        }
        if (activeUc.checks.consumo500 === false && !activeUc.checks.explicacaoConsumo500) {
            isComplete = false; missingFields.push('Justificativa Consumo < 500');
        }
        if (activeUc.checks.possuiGD === true && !activeUc.checks.explicacaoGD) {
            isComplete = false; missingFields.push('Detalhes GD');
        }

        // Regra única do consumo/compensável — mesma função que a tela read-only, o
        // Step 2, o Step 4 e o wizard usam (src/lib/analiseTecnicaConsumo.ts).
        const consumo = calcularConsumoUc({
            historico: history,
            tipoLigacao: activeUc.tipoFornecimento,
            possuiGD: activeUc.checks.possuiGD,
        });
        const { avgConsumo: ucAvgConsumo, avgInjecao: ucAvgInjecao, avgCompensavel: ucAvgCompensavel, avgNaoCompensado: ucAvgNaoCompensado, mesesDescartados: excludedCount } = consumo;

        const validHistory = history.filter((r: any) => !r.semRegistro);

        const delays = validHistory.map(r => Number(r.delayDays) || 0);
        const totalDelays = delays.reduce((acc, d) => acc + d, 0);
        // Média sobre TODOS os meses VÁLIDOS
        const ucAvgDelay = validHistory.length > 0 ? Math.round(totalDelays / validHistory.length) : 0;
        const hasGraveOcurrence = validHistory.some(r => r.ocorrencia && r.ocorrencia.toLowerCase().match(/renegocia|revelia|parcelamento|corte/));

        // Classificação baseada na MÉDIA de atraso (não no pior mês individual)
        let cls = 'Excelente';
        if (hasGraveOcurrence) cls = 'Calote';
        else if (ucAvgDelay > 60) cls = 'Calote';
        else if (ucAvgDelay > 30) cls = 'Péssimo';
        else if (ucAvgDelay > 15) cls = 'Ruim';
        else if (ucAvgDelay > 5) cls = 'Médio';
        else if (ucAvgDelay > 0) cls = 'Bom';

        let ucStatus = 'Aprovada';
        let ucReasons: string[] = [];

        if (!isComplete) {
            ucStatus = 'Pendente';
            ucReasons.push(`Pendências: ${missingFields.join(', ')}`);
        } else {
            // Auto-rejection rules
            if (activeUc.checks.beneficio === true) {
                ucStatus = 'Reprovada';
                ucReasons.push('Recebendo benefício (Baixa Renda, etc).');
            }
            if (activeUc.checks.injetando === true) {
                ucStatus = 'Reprovada';
                ucReasons.push('Terceiros injetando energia na UC.');
            }
            if (activeUc.checks.atualizada === false) {
                ucStatus = 'Reprovada';
                ucReasons.push('Fatura não está atualizada na Equatorial.');
            }
            if (activeUc.checks.mesmoTitular === false) {
                ucStatus = 'Reprovada';
                ucReasons.push('Titular não confere com o termo.');
            }
            if (activeUc.checks.possuiGD && ucAvgCompensavel < 1000) {
                ucStatus = 'Reprovada';
                ucReasons.push(`Possui GD e compensável (${ucAvgCompensavel} kWh) abaixo de 1.000 kWh.`);
            }

            // Payment classification risks (secondary)
            if (ucStatus !== 'Reprovada') {
                if (cls === 'Calote' || cls === 'Péssimo') {
                    ucStatus = 'Reprovada';
                    ucReasons.push('Classificação de pagamento inaceitável.');
                }
            }
            if (activeUc.grupo === 'A (Média/Alta Tensão)') {
                ucReasons.push('Unidades do Grupo A demandam análise específica de demanda contratada.');
            }
            if (ucAvgCompensavel < 500 && !activeUc.checks.consumo500) {
                ucReasons.push('Consumo compensável abaixo de 500 kWh (Exceção justificada).');
            }
        }

        return {
            ucAvgConsumo,
            ucAvgInjecao,
            ucAvgCompensavel,
            ucAvgNaoCompensado,
            ucAvgDelay,
            cls,
            ucStatus,
            ucReasons,
            isComplete,
            excludedCount,
            consumo
        };
    };

    const activeUcCalc = getActiveUcCalc();

    // UC-specific divergence: compare vendor-registered UC consumption vs analyst calculated
    const ucVendorConsumo = uc.consumo_medio_kwh ?? 0;
    const ucDivergence = activeUcCalc.isComplete && activeUcCalc.ucAvgConsumo > 0 && Math.abs(activeUcCalc.ucAvgConsumo - ucVendorConsumo) > 50;

    const handleSave = () => {
        onSave(uc.id, {
            dados_tecnicos_equatorial: {
                ...uc.dados_tecnicos_equatorial,
                titularidade: activeUc.titularidade,
                nParceiro: activeUc.nParceiro,
                novoNumeroUc: activeUc.novoNumeroUc,
                grupo: activeUc.grupo,
                tipoFornecimento: activeUc.tipoFornecimento,
                dataLeitura: activeUc.dataLeitura,
                dataApresentacao: activeUc.dataApresentacao,
                dataVencimento: activeUc.dataVencimento,
                dataProcessamento: activeUc.dataProcessamento,
                checks: activeUc.checks,
                historyTable: activeUc.historyTable,
                status: activeUcCalc.ucStatus,
                // Persist uploaded fatura URL
                faturaUrl: uploadedFile?.url || (uc.dados_tecnicos_equatorial as any)?.faturaUrl || null,
                faturaNome: uploadedFile?.name || (uc.dados_tecnicos_equatorial as any)?.faturaNome || null,
                faturaType: uploadedFile?.type || (uc.dados_tecnicos_equatorial as any)?.faturaType || null,
            } as any,
            titular_fatura: activeUc.titularidade,
            num_parceiro: activeUc.nParceiro,
            numero_uc: activeUc.unidadeConsumidora,
            grupo_tarifario: activeUc.grupo || null,
            tipo_ligacao: activeUc.tipoFornecimento || null,
            // DO NOT save dataLeitura etc. to root UC columns — they are type date and we store DD only
            data_nascimento_cliente: activeUc.dataNascimentoCliente || null,
            nome_cliente: activeUc.titularidade || uc.nome_cliente,
        } as any);
    };

    const processFileUpload = async (file: File) => {
        setUploading(true);
        try {
            let persistentUrl = '';

            // #6304: isto mandava a fatura para o webhook de "Pasta e anexos da
            // negociação" com outro formato (negociacao_id/file), que o fluxo n8n não
            // entende — cada envio virava erro lá e caía neste Storage de qualquer jeito.
            // Vai direto para o Storage.
            {
                const fileExt = file.name.split('.').pop() || 'pdf';
                const filePath = `faturas/${uc.negociacao_id}/${uc.id}_${Date.now()}.${fileExt}`;
                const { error: uploadError } = await supabase.storage
                    .from('analise-cadastral')
                    .upload(filePath, file, { upsert: true });

                if (uploadError) throw uploadError;

                const { data: urlData } = supabase.storage
                    .from('analise-cadastral')
                    .getPublicUrl(filePath);
                persistentUrl = urlData?.publicUrl || '';
            }

            if (!persistentUrl) throw new Error('Não foi possível obter URL persistente.');

            setUploadedFile({ name: file.name, url: persistentUrl, type: file.type });
            addToast('Fatura atualizada enviada.', 'success');
        } catch (error) {
            console.error('Error uploading file:', error);
            addToast('Erro ao enviar a fatura.', 'error');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }

        // Extração roda DEPOIS do upload, e nunca o desfaz: se o robô falhar, o
        // PDF continua anexado e o analista preenche à mão como sempre fez.
        await extrairSugestao(file);
    };

    const extrairSugestao = async (file: File) => {
        setExtraindo(true);
        try {
            const resp = await extrairFatura(file, null, 'analise-cadastral');
            if (!resp.ok || !resp.estruturado) {
                addToast(resp.error || 'O robô não conseguiu ler esta fatura — preencha manualmente.', 'warning');
                return;
            }
            const dados = mapEstruturadoParaAnaliseTecnica(resp.estruturado, {
                incluirNascimento: negociacao?.tipo_cliente === 'PF',
            });
            if (!dados.campos.length && !dados.historico.length) {
                addToast('O robô leu a fatura, mas não achou campos aproveitáveis.', 'warning');
                return;
            }
            setSugestao({ dados, reconciliacao: resp.reconciliacao ?? null });
        } catch (e: any) {
            console.error('[UcAnalysisModal] extração falhou:', e);
            addToast('Não foi possível ler a fatura automaticamente — preencha manualmente.', 'warning');
        } finally {
            setExtraindo(false);
        }
    };

    const aplicarSugestao = (a: AplicacaoSugestao) => {
        setActiveUc(prev => {
            const proximo: typeof prev = {
                ...prev,
                ...a.campos,
                checks: { ...prev.checks, ...a.checks },
            };

            if (a.historico) {
                // Casa por MÊS, não por índice: se o analista já digitou vencimento/
                // pagamento numa linha, aquelas colunas têm que sobreviver ao preenchimento.
                const porMes = new Map(prev.historyTable.map((l: any) => [l.month, l]));
                const usadas = new Set<string>();
                const linhas = a.historico.map(h => {
                    const existente = porMes.get(h.month);
                    if (existente) usadas.add(h.month);
                    return { ...(existente || defaultHistoryTable[0]), month: h.month, consumo: h.consumo };
                });
                // Preserva linhas já preenchidas que o robô não trouxe (meses lidos do portal).
                const remanescentes = prev.historyTable.filter((l: any) => l.month && !usadas.has(l.month) && !a.historico!.some(h => h.month === l.month));
                const combinadas = [...linhas, ...remanescentes].slice(0, 12);
                while (combinadas.length < 12) combinadas.push({ ...defaultHistoryTable[0] });
                proximo.historyTable = combinadas.map((l: any) => ({
                    ...l,
                    delayDays: calculateDelay(l.vencimento, l.pagamento, l.emAberto),
                }));
            }

            return proximo;
        });
        setSugestao(null);
        addToast('Dados da fatura aplicados. Confira antes de salvar.', 'success');
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processFileUpload(file);
    };

    const handleFileDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processFileUpload(file);
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[99999] flex items-center justify-center bg-slate-900/60 dark:bg-black/70 backdrop-blur-sm p-4 overflow-y-auto animate-fade-in"
            onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
            onClick={() => { if (backdropMouseDownRef.current) onClose(); backdropMouseDownRef.current = false; }}
        >
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl shadow-slate-900/20 w-full max-w-7xl my-8 relative flex flex-col border border-slate-200/80 dark:border-slate-800 animate-modal-in" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800 sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-primary/10 dark:bg-primary/15 rounded-xl border border-primary/20">
                            <Zap size={18} className="text-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">Análise Técnica</h2>
                            <div className="flex items-center gap-2 mt-0.5">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">UC {uc.numero_uc}</p>
                                {uc.outro_numero_uc && (
                                    <span className="text-[9px] font-bold text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded tracking-wider uppercase">
                                        {uc.outro_numero_uc}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all text-slate-400 hover:text-red-500 group">
                        <X size={18} className="transition-transform group-hover:rotate-90" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto bg-slate-50/30 dark:bg-slate-900/50" style={{ maxHeight: 'calc(100vh - 200px)' }}>

                    {/* Alerta de Divergência de Consumo na UC */}
                    {ucDivergence && (
                        <div className="mb-6 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/50 rounded-2xl p-5 flex items-start gap-4 shadow-sm animate-fade-in">
                            <div className="p-2 bg-orange-100 dark:bg-orange-900/40 rounded-xl shrink-0">
                                <FileWarning size={22} className="text-orange-600 dark:text-orange-400" />
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-orange-800 dark:text-orange-400 mb-1">Divergência de Consumo nesta UC</h4>
                                <p className="text-xs text-orange-700 dark:text-orange-300 font-medium">
                                    O vendedor registrou <strong className="font-extrabold text-orange-900 dark:text-orange-200">{ucVendorConsumo.toLocaleString('pt-BR')} kWh</strong> para esta UC.
                                    Porém, a média calculada dos últimos 12 meses é <strong className="font-extrabold text-orange-900 dark:text-orange-200">{activeUcCalc.ucAvgConsumo.toLocaleString('pt-BR')} kWh</strong> (diferença de {Math.abs(activeUcCalc.ucAvgConsumo - ucVendorConsumo).toLocaleString('pt-BR')} kWh).
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Leitura do robô determinístico, aguardando revisão */}
                    {sugestao && (
                        <SugestaoFaturaPanel
                            sugestao={sugestao.dados}
                            reconciliacao={sugestao.reconciliacao}
                            atuais={{
                                titularidade: activeUc.titularidade,
                                nParceiro: activeUc.nParceiro,
                                unidadeConsumidora: activeUc.unidadeConsumidora,
                                grupo: activeUc.grupo,
                                tipoFornecimento: activeUc.tipoFornecimento,
                                dataLeitura: activeUc.dataLeitura,
                                dataApresentacao: activeUc.dataApresentacao,
                                dataVencimento: activeUc.dataVencimento,
                                dataNascimentoCliente: activeUc.dataNascimentoCliente,
                            }}
                            checksAtuais={activeUc.checks}
                            linhasHistoricoPreenchidas={activeUc.historyTable.filter((l: any) => l.month).length}
                            onAplicar={aplicarSugestao}
                            onIgnorar={() => setSugestao(null)}
                        />
                    )}

                    {/* Top Details */}
                    <div className="bg-white dark:bg-slate-900/80 p-6 rounded-2xl shadow-sm border border-slate-200/80 dark:border-slate-800 mb-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                <Zap className="text-amber-500" /> Dados Básicos da Fatura
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept=".pdf,.png,.jpg,.jpeg"
                                    onChange={handleFileUpload}
                                />
                                <div
                                    className={`flex items-center gap-2 px-4 py-2.5 border-2 border-dashed rounded-xl cursor-pointer transition-all text-sm font-bold shadow-sm
                                        ${dragging ? 'border-primary bg-primary/5 scale-[1.02]' : 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-400'}
                                        ${(uploading || extraindo) ? 'opacity-50 pointer-events-none' : ''}`}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                                    onDragLeave={() => setDragging(false)}
                                    onDrop={handleFileDrop}
                                >
                                    {(uploading || extraindo) ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                                    {uploading ? 'Enviando...'
                                        : extraindo ? 'Robô lendo a fatura...'
                                        : dragging ? 'Solte aqui!'
                                        : 'Carregar Fatura Atualizada'}
                                </div>
                                {uploadedFile && (
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-lg animate-fade-in">
                                        <button
                                            type="button"
                                            onClick={() => window.open(uploadedFile.url, '_blank')}
                                            className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
                                            title="Abrir fatura em nova aba"
                                        >
                                            {uploadedFile.type.startsWith('image/') ? (
                                                <img src={uploadedFile.url} alt="Preview" className="w-8 h-8 rounded object-cover border border-emerald-200" />
                                            ) : (
                                                <FileImage size={16} className="text-emerald-600" />
                                            )}
                                            <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400 max-w-[120px] truncate">{uploadedFile.name}</span>
                                            <ExternalLink size={11} className="text-emerald-500 shrink-0" />
                                        </button>
                                        <button
                                            onClick={() => { setUploadedFile(null); }}
                                            className="p-0.5 hover:bg-red-100 rounded text-slate-400 hover:text-red-500 transition-colors"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Nome do Titular</label>
                                <input type="text" value={activeUc.titularidade} onChange={e => handleUcFieldChange('titularidade', e.target.value)} className="w-full p-2 border border-slate-200 rounded-xl dark:border-slate-700 focus:ring-2 focus:ring-primary/40 focus:border-primary/40 outline-none text-sm font-medium text-slate-700 dark:text-slate-200 dark:bg-slate-800/40 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600" placeholder="Nome na fatura" />
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Unidade Consumidora</label>
                                <input type="text" value={activeUc.unidadeConsumidora} onChange={e => handleUcFieldChange('unidadeConsumidora', e.target.value)} className="w-full p-2 border border-slate-200 rounded-xl dark:border-slate-700 focus:ring-2 focus:ring-primary/40 focus:border-primary/40 outline-none text-sm font-medium text-slate-700 dark:text-slate-200 dark:bg-slate-800/40 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600" placeholder="Ex: 999999" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Novo Nº UC</label>
                                <input type="text" value={activeUc.novoNumeroUc} onChange={e => handleUcFieldChange('novoNumeroUc', e.target.value)} className="w-full p-2 border border-slate-200 rounded-xl dark:border-slate-700 focus:ring-2 focus:ring-primary/40 focus:border-primary/40 outline-none text-sm font-medium text-slate-700 dark:text-slate-200 dark:bg-slate-800/40 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600" placeholder="Novo número (ANEEL)" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Grupo Tarifário</label>
                                <select value={activeUc.grupo} onChange={e => handleUcFieldChange('grupo', e.target.value)} className="w-full p-2 border border-slate-200 rounded-xl dark:border-slate-700 focus:ring-2 focus:ring-primary/40 focus:border-primary/40 outline-none text-sm font-medium text-slate-700 dark:text-slate-200 dark:bg-slate-800/40 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600 bg-white">
                                    <option value="">Selecione...</option>
                                    <option value="A1">A1</option>
                                    <option value="A2">A2</option>
                                    <option value="A3">A3</option>
                                    <option value="A4">A4</option>
                                    <option value="AS">AS</option>
                                    <option value="B1">B1</option>
                                    <option value="B2">B2</option>
                                    <option value="B3">B3</option>
                                    <option value="B4">B4</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Tipo de Ligação</label>
                                <select value={activeUc.tipoFornecimento} onChange={e => handleUcFieldChange('tipoFornecimento', e.target.value)} className="w-full p-2 border border-slate-200 rounded-xl dark:border-slate-700 focus:ring-2 focus:ring-primary/40 focus:border-primary/40 outline-none text-sm font-medium text-slate-700 dark:text-slate-200 dark:bg-slate-800/40 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600 bg-white">
                                    <option value="">Selecione...</option>
                                    <option value="Monofásico">Monofásico</option>
                                    <option value="Bifásico">Bifásico</option>
                                    <option value="Trifásico">Trifásico</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Dia Leitura</label>
                                <input type="text" inputMode="numeric" maxLength={2} value={activeUc.dataLeitura} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); handleUcFieldChange('dataLeitura', v); }} className="w-full p-2 border border-slate-200 rounded-xl dark:border-slate-700 focus:ring-2 focus:ring-primary/40 focus:border-primary/40 outline-none text-sm font-medium text-slate-700 dark:text-slate-200 dark:bg-slate-800/40 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600 text-center" placeholder="DD" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Dia Apresentação</label>
                                <input type="text" inputMode="numeric" maxLength={2} value={activeUc.dataApresentacao} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); handleUcFieldChange('dataApresentacao', v); }} className="w-full p-2 border border-slate-200 rounded-xl dark:border-slate-700 focus:ring-2 focus:ring-primary/40 focus:border-primary/40 outline-none text-sm font-medium text-slate-700 dark:text-slate-200 dark:bg-slate-800/40 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600 text-center" placeholder="DD" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Dia Vencimento</label>
                                <input type="text" inputMode="numeric" maxLength={2} value={activeUc.dataVencimento} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); handleUcFieldChange('dataVencimento', v); }} className="w-full p-2 border border-slate-200 rounded-xl dark:border-slate-700 focus:ring-2 focus:ring-primary/40 focus:border-primary/40 outline-none text-sm font-medium text-slate-700 dark:text-slate-200 dark:bg-slate-800/40 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600 text-center" placeholder="DD" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Dia Processamento</label>
                                <input type="text" inputMode="numeric" maxLength={2} value={activeUc.dataProcessamento} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); handleUcFieldChange('dataProcessamento', v); }} className="w-full p-2 border border-slate-200 rounded-xl dark:border-slate-700 focus:ring-2 focus:ring-primary/40 focus:border-primary/40 outline-none text-sm font-medium text-slate-700 dark:text-slate-200 dark:bg-slate-800/40 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600 text-center" placeholder="DD" />
                            </div>
                            {negociacao?.tipo_cliente === 'PF' && (
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase">Data de Nascimento</label>
                                    <input type="date" value={activeUc.dataNascimentoCliente} onChange={e => handleUcFieldChange('dataNascimentoCliente', e.target.value)} className="w-full p-2 border border-slate-200 rounded-xl dark:border-slate-700 focus:ring-2 focus:ring-primary/40 focus:border-primary/40 outline-none text-sm font-medium text-slate-700 dark:text-slate-200 dark:bg-slate-800/40 transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Checklists Modernos */}
                    <div className="bg-white dark:bg-slate-900/80 p-6 rounded-2xl shadow-sm border border-slate-200/80 dark:border-slate-800 mb-6">
                        <div className="flex items-center gap-2 mb-6">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                <Info className="text-primary" /> Conferência e Regras de Negócio
                            </h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            <BooleanToggle label="Fatura atualizada na Equatorial?" value={activeUc.checks.atualizada} onChange={v => handleUcCheckChange('atualizada', v)} alertWhen="false" />

                            <div className="flex flex-col gap-2">
                                <BooleanToggle label="A Unidade é Grupo B?" value={activeUc.checks.grupoB} onChange={v => handleUcCheckChange('grupoB', v)} />
                                {activeUc.checks.grupoB === false && (
                                    <div className="animate-fade-in origin-top">
                                        <textarea placeholder="Justifique e detalhe a aprovação desta conta Grupo A *" value={activeUc.checks.explicacaoGrupoB || ''} onChange={e => handleUcCheckChange('explicacaoGrupoB', e.target.value)} className="w-full text-xs p-3 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/10 text-red-800 dark:text-red-200 placeholder:text-red-400 dark:placeholder:text-red-400 focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none resize-none transition-all shadow-sm" rows={2}></textarea>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col gap-2">
                                <BooleanToggle label="Consumo Compensável > 500 kWh?" value={activeUc.checks.consumo500} onChange={v => handleUcCheckChange('consumo500', v)} />
                                {activeUc.checks.consumo500 === false && (
                                    <div className="animate-fade-in origin-top">
                                        <textarea placeholder="Motivo de aceitar UC abaixo do limite de 500 kWh *" value={activeUc.checks.explicacaoConsumo500 || ''} onChange={e => handleUcCheckChange('explicacaoConsumo500', e.target.value)} className="w-full text-xs p-3 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/10 text-red-800 dark:text-red-200 placeholder:text-red-400 dark:placeholder:text-red-400 focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none resize-none transition-all shadow-sm" rows={2}></textarea>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col gap-2">
                                <BooleanToggle label="A Unidade possui GD instalada?" value={activeUc.checks.possuiGD} onChange={v => handleUcCheckChange('possuiGD', v)} alertWhen="true" />
                                {activeUc.checks.possuiGD === true && (
                                    <div className="animate-fade-in origin-top">
                                        <textarea placeholder="Motivo de aceitar unidade com GD instalada *" value={activeUc.checks.explicacaoGD || ''} onChange={e => handleUcCheckChange('explicacaoGD', e.target.value)} className="w-full text-xs p-3 rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10 text-amber-800 dark:text-amber-200 placeholder:text-amber-500 dark:placeholder:text-amber-500 focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none resize-none transition-all shadow-sm" rows={2}></textarea>
                                    </div>
                                )}
                            </div>

                            <BooleanToggle label="Recebendo algum Benefício (Baixa Renda, etc)?" value={activeUc.checks.beneficio} onChange={v => handleUcCheckChange('beneficio', v)} alertWhen="true" />
                            <BooleanToggle label="Há terceiros injetando energia na UC?" value={activeUc.checks.injetando} onChange={v => handleUcCheckChange('injetando', v)} alertWhen="true" />

                            <BooleanToggle label="Titular confere exatamente com o termo?" value={activeUc.checks.mesmoTitular} onChange={v => handleUcCheckChange('mesmoTitular', v)} alertWhen="false" />

                            {activeUc.checks.injetando && (
                                <div className="mt-1 pt-4 border-t border-slate-100 dark:border-slate-800/50 animate-fade-in origin-top md:col-span-2 lg:col-span-3">
                                    <BooleanToggle label="Documento de distrato de geração distribuída cancelado recebido?" value={activeUc.checks.distratoRecebido} onChange={v => handleUcCheckChange('distratoRecebido', v)} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Status Banner Automático da UC */}
                    {activeUcCalc.ucStatus !== 'Aprovada' && (
                        <div className={`mb-6 p-4 rounded-xl border flex flex-col items-start ${activeUcCalc.ucStatus === 'Pendente' && !activeUcCalc.isComplete ? 'bg-slate-50 border-slate-300 dark:bg-slate-800/60 dark:border-slate-700' : activeUcCalc.ucStatus === 'Reprovada' ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-900/50' : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-900/50'}`}>
                            <div className="flex items-center gap-2 mb-2">
                                {activeUcCalc.ucStatus === 'Pendente' && !activeUcCalc.isComplete ? <Info size={18} className="text-slate-500" /> : activeUcCalc.ucStatus === 'Reprovada' ? <XSquare size={18} className="text-red-500" /> : <AlertCircle size={18} className="text-amber-500" />}
                                <span className={`font-bold ${activeUcCalc.ucStatus === 'Pendente' && !activeUcCalc.isComplete ? 'text-slate-700 dark:text-slate-300' : activeUcCalc.ucStatus === 'Reprovada' ? 'text-red-800 dark:text-red-400' : 'text-amber-800 dark:text-amber-400'}`}>Status: {activeUcCalc.ucStatus}{!activeUcCalc.isComplete ? ' (Falta Preencher)' : ''}</span>
                            </div>
                            <ul className="list-disc list-inside text-sm font-medium ml-1 space-y-1">
                                {activeUcCalc.ucReasons.map((r, idx) => <li key={idx} className={activeUcCalc.ucStatus === 'Pendente' && !activeUcCalc.isComplete ? 'text-slate-600 dark:text-slate-400' : activeUcCalc.ucStatus === 'Reprovada' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}>{r}</li>)}
                            </ul>
                        </div>
                    )}

                    {/* 12 Months Grid */}
                    <div className="bg-white dark:bg-slate-900/80 rounded-2xl shadow-sm border border-slate-200/80 dark:border-slate-800 overflow-hidden mb-6">
                        <div className="px-5 py-5 bg-slate-50/80 dark:bg-slate-800/40 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center flex-wrap gap-4">
                            <div className="flex items-center gap-2.5">
                                <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                                    <Activity size={14} className="text-primary" />
                                </div>
                                <h3 className="font-bold text-sm text-slate-700 dark:text-slate-200">Histórico de 12 Meses</h3>
                            </div>
                            <div className="flex gap-3 text-xs flex-wrap">
                                <span className={`px-3 py-1.5 rounded-full border shadow-sm font-bold ${ucDivergence ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200/80 dark:border-orange-800/50 text-orange-700 dark:text-orange-400' : 'bg-slate-100 dark:bg-slate-800/80 border-slate-200/80 dark:border-slate-700 text-slate-600 dark:text-slate-300'}`}>
                                    Vendedor Registrou (UC): {ucVendorConsumo > 0 ? `${ucVendorConsumo.toLocaleString('pt-BR')} kWh` : '- kWh'}
                                </span>
                                <span title={`Média dos ${activeUcCalc.consumo.mesesBase} meses considerados.`} className="bg-white dark:bg-slate-800 px-3 py-1.5 rounded-full border border-slate-200/80 dark:border-slate-700 text-slate-600 dark:text-slate-300 shadow-sm font-bold">Consumo Médio Calculado: <span className="text-primary">{activeUcCalc.ucAvgConsumo} kWh</span></span>
                                {activeUcCalc.consumo.janelaCurta && (
                                    <span
                                        className="bg-sky-50 dark:bg-sky-900/20 px-3 py-1.5 rounded-full border border-sky-200/80 dark:border-sky-800/50 text-sky-700 dark:text-sky-400 font-bold shadow-sm flex items-center gap-1"
                                        title={`Os 3 meses mais recentes têm média de ${activeUcCalc.consumo.degrau.mediaRecente} kWh contra ${activeUcCalc.consumo.degrau.mediaAnterior} kWh dos meses anteriores. Variação acima de 30% indica que o cliente mudou de patamar e se manteve nele — a média dos 12 meses descreveria um consumo que não existe mais, e o rateio alocaria energia por ele. Dentro desta janela a regra pró-cliente não se aplica.`}
                                    >
                                        {activeUcCalc.consumo.degrau.direcao === 'queda' ? <TrendingDown size={12} /> : <TrendingUp size={12} />} Últimos 3 meses
                                        <span className="font-normal text-[10px] uppercase opacity-70 ml-1">
                                            mudança de patamar {activeUcCalc.consumo.degrau.variacaoPercent! > 0 ? '+' : ''}{activeUcCalc.consumo.degrau.variacaoPercent}%
                                        </span>
                                    </span>
                                )}
                                {activeUc.checks.possuiGD && (
                                    <span
                                        title={`Média dos ${activeUcCalc.consumo.mesesComInjecao} ${activeUcCalc.consumo.mesesComInjecao === 1 ? 'mês' : 'meses'} em que houve injeção — mês zerado não entra, igual à regra do consumo médio. No compensável essa energia entra diluída no período (${activeUcCalc.consumo.avgInjecaoPeriodo} kWh/mês), porque o crédito da GD que sobra em um mês abate os meses seguintes.`}
                                        className="bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 rounded-full border border-amber-200/80 dark:border-amber-800/50 text-amber-700 dark:text-amber-400 shadow-sm font-bold"
                                    >
                                        Injeção Média: {activeUcCalc.ucAvgInjecao} kWh
                                        {activeUcCalc.consumo.mesesComInjecao < activeUcCalc.consumo.mesesBase ? (
                                            <span className="font-normal text-[10px] opacity-70 ml-1">
                                                em {activeUcCalc.consumo.mesesComInjecao} de {activeUcCalc.consumo.mesesBase} meses
                                            </span>
                                        ) : null}
                                    </span>
                                )}
                                <span title={`Custo de disponibilidade (${activeUcCalc.consumo.custoDisponibilidade} kWh)${activeUc.checks.possuiGD ? ' + energia já compensada pela GD do cliente' : ''}.`} className="bg-red-50 dark:bg-red-900/20 px-3 py-1.5 rounded-full border border-red-200/80 dark:border-red-800/50 text-red-700 dark:text-red-400 shadow-sm font-bold">Não Compensado: {activeUcCalc.ucAvgNaoCompensado} kWh</span>
                                <span title="O que a Vigor consegue de fato compensar por mês." className="bg-green-50 dark:bg-green-900/20 px-3 py-1.5 rounded-full border border-green-200/80 dark:border-green-800/40 text-green-700 dark:text-green-400 font-bold shadow-sm">KWH Compensável: {activeUcCalc.ucAvgCompensavel} kWh</span>
                                {activeUcCalc.excludedCount > 0 && (
                                    <span className="bg-yellow-50 dark:bg-yellow-900/20 px-3 py-1.5 rounded-full border border-yellow-200/80 dark:border-yellow-800/50 text-yellow-700 dark:text-yellow-400 font-bold shadow-sm flex items-center gap-1" title="Para proteger o cliente, valores que puxavam a média significativamente para baixo (abaixo da média geral) foram removidos do cálculo final.">
                                        <AlertCircle size={12} /> {activeUcCalc.excludedCount} {activeUcCalc.excludedCount === 1 ? 'mês ignorado' : 'meses ignorados'} <span className="font-normal text-[10px] uppercase opacity-70 ml-1">Regra PRÓ-CLIENTE</span>
                                    </span>
                                )}
                                {activeUcCalc.consumo.injecaoIgnorada > 0 && (
                                    <span className="bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 rounded-full border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 font-bold shadow-sm flex items-center gap-1" title="Há injeção lançada no histórico, mas o checklist não marca GD — essa energia está fora do cálculo.">
                                        <AlertTriangle size={12} /> Injeção lançada sem marcar GD
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setRegraAberta(v => !v)}
                                    aria-expanded={regraAberta}
                                    className="px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 dark:bg-primary/10 text-primary font-bold shadow-sm flex items-center gap-1.5 hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors"
                                >
                                    <Calculator size={12} /> Como é calculado
                                    <ChevronDown size={12} className={`transition-transform ${regraAberta ? 'rotate-180' : ''}`} />
                                </button>
                            </div>
                        </div>

                        {regraAberta ? (
                            <RegraCalculoConsumo calculo={activeUcCalc.consumo} />
                        ) : null}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left whitespace-nowrap">
                                <thead className="bg-slate-50/50 dark:bg-slate-800/20 border-b border-slate-100 dark:border-slate-800">
                                    <tr>
                                        <th className="p-3 w-16 text-center text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em]" title="Sem Registro">N/D</th>
                                        <th className="p-3 w-20 text-center text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em]">Mês (mm/aa)</th>
                                        <th className="p-3 w-28 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em]">Consumo (kWh)</th>
                                        {activeUc.checks.possuiGD && <th className="p-3 bg-amber-50/50 dark:bg-amber-900/10 w-28 text-[10px] font-bold text-amber-500 uppercase tracking-[0.1em]">Injeção (kWh)</th>}
                                        <th className="p-3 w-36 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em]">Vencimento (*)</th>
                                        <th className="p-3 w-36 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em]">Pagamento</th>
                                        <th className="p-3 text-center w-20 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em]">Atraso (d)</th>
                                        <th className="p-3 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em]">Ocorrências</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeUc.historyTable.map((row, idx) => (
                                        <tr key={idx} className={`border-b border-slate-50 dark:border-slate-800/60 transition-colors ${row.semRegistro ? 'opacity-50 hover:opacity-100 bg-slate-50/20 dark:bg-slate-800/10' : 'hover:bg-primary/[0.02] dark:hover:bg-primary/[0.04]'}`}>
                                            <td className="p-2 text-center align-middle bg-slate-50/50 dark:bg-slate-800/30">
                                                <input
                                                    type="checkbox"
                                                    checked={row.semRegistro}
                                                    onChange={e => handleHistoryChange(idx, 'semRegistro', e.target.checked)}
                                                    className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/40 cursor-pointer"
                                                    title="Marcar se este mês não possui registro de consumo"
                                                />
                                            </td>
                                            <td className="p-2 bg-slate-50/50 dark:bg-slate-800/30">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={5}
                                                    value={row.month}
                                                    onChange={e => {
                                                        const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                                                        let formatted = digits;
                                                        if (digits.length > 2) formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
                                                        handleHistoryChange(idx, 'month', formatted);
                                                    }}
                                                    disabled={row.semRegistro}
                                                    className={`w-full p-1.5 border rounded-lg focus:ring-1 focus:ring-primary outline-none text-center text-xs font-bold dark:bg-slate-800 transition-colors shadow-sm ${row.semRegistro ? 'opacity-50 cursor-not-allowed' : !row.month ? 'border-slate-300 dark:border-slate-600 text-slate-400' : 'border-primary/30 text-slate-700 dark:text-slate-200'}`}
                                                    placeholder="mm/aa"
                                                />
                                            </td>
                                            <td className="p-2">
                                                <input type="text" inputMode="decimal" value={row.consumo ?? ''} onChange={e => { const v = e.target.value.replace(/[^\d.,]/g, ''); handleHistoryChange(idx, 'consumo', v); }} onBlur={e => { let v = e.target.value; if (v.endsWith('.00')) handleHistoryChange(idx, 'consumo', v.slice(0, -3)); }} disabled={row.semRegistro} className={`w-full p-2 border rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-right placeholder:text-slate-300 dark:bg-slate-800 transition-colors shadow-sm font-medium ${row.semRegistro ? 'opacity-50 cursor-not-allowed' : (row.consumo === null || row.consumo.toString() === '') ? 'border-red-300 bg-red-50/30 dark:border-red-500/50' : 'border-slate-200 dark:border-slate-700'}`} placeholder="0" />
                                            </td>
                                            {activeUc.checks.possuiGD && (
                                                <td className="p-2 bg-amber-50/30 dark:bg-amber-900/5">
                                                    <input type="text" inputMode="decimal" value={row.injecao ?? ''} onChange={e => { const v = e.target.value.replace(/[^\d.,]/g, ''); handleHistoryChange(idx, 'injecao', v); }} onBlur={e => { let v = e.target.value; if (v.endsWith('.00')) handleHistoryChange(idx, 'injecao', v.slice(0, -3)); }} disabled={row.semRegistro} className={`w-full p-2 border border-amber-200 dark:border-amber-700/50 rounded-lg focus:ring-1 focus:ring-amber-500 outline-none text-right placeholder:text-amber-200 dark:bg-slate-800 transition-colors shadow-sm font-medium ${row.semRegistro ? 'opacity-50 cursor-not-allowed' : ''}`} placeholder="0" />
                                                </td>
                                            )}
                                            <td className="p-2">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={10}
                                                    value={row.vencimento}
                                                    onChange={e => handleHistoryChange(idx, 'vencimento', formatDateInput(e.target.value))}
                                                    onPaste={e => {
                                                        const result = handleDatePaste(e);
                                                        if (result) handleHistoryChange(idx, 'vencimento', result);
                                                    }}
                                                    disabled={row.semRegistro}
                                                    className={`w-full p-2 border rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-slate-600 dark:text-slate-300 dark:bg-slate-800 shadow-sm transition-colors text-xs font-medium ${row.semRegistro ? 'opacity-50 cursor-not-allowed' : !row.vencimento ? 'border-red-300 bg-red-50/30 dark:border-red-500/50' : 'border-slate-200 dark:border-slate-700'}`}
                                                    placeholder="dd/mm/aaaa"
                                                />
                                            </td>
                                            <td className="p-2">
                                                <div className="flex flex-col gap-1">
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        maxLength={10}
                                                        value={row.pagamento}
                                                        onChange={e => handleHistoryChange(idx, 'pagamento', formatDateInput(e.target.value))}
                                                        onPaste={e => {
                                                            const result = handleDatePaste(e);
                                                            if (result) handleHistoryChange(idx, 'pagamento', result);
                                                        }}
                                                        disabled={(row as any).emAberto || row.semRegistro}
                                                        className={`w-full p-2 border rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none text-slate-600 dark:text-slate-300 dark:bg-slate-800 shadow-sm transition-colors text-xs font-medium ${row.semRegistro ? 'opacity-50 cursor-not-allowed' : (row as any).emAberto ? 'opacity-40 cursor-not-allowed' : !row.pagamento && !row.ocorrencia ? 'border-red-300 bg-red-50/30 dark:border-red-500/50' : 'border-slate-200 dark:border-slate-700'}`}
                                                        placeholder="dd/mm/aaaa"
                                                    />
                                                    <label className={`flex items-center gap-1.5 cursor-pointer ${row.semRegistro ? 'opacity-50 pointer-events-none' : ''}`}>
                                                        <input type="checkbox" checked={(row as any).emAberto || false} onChange={e => handleHistoryChange(idx, 'emAberto', e.target.checked)} disabled={row.semRegistro} className="w-3.5 h-3.5 rounded border-slate-300 text-primary focus:ring-primary/40" />
                                                        <span className="text-[10px] font-bold text-slate-400">Em Aberto</span>
                                                    </label>
                                                </div>
                                            </td>
                                            <td className="p-2 text-center">
                                                {!row.semRegistro && (
                                                    <span className={`inline-flex items-center justify-center w-full max-w-[40px] py-1 rounded-md font-bold text-xs ${row.delayDays > 15 ? 'bg-red-100 text-red-700' : row.delayDays > 5 ? 'bg-amber-100 text-amber-700' : row.delayDays > 0 ? 'bg-slate-100 text-slate-700' : 'text-emerald-600'}`}>
                                                        {row.delayDays}
                                                    </span>
                                                )}
                                                {row.semRegistro && (
                                                    <span className="inline-flex items-center justify-center w-full max-w-[40px] py-1 rounded-md font-bold text-xs text-slate-400">-</span>
                                                )}
                                            </td>
                                            <td className="p-2">
                                                <input type="text" value={row.ocorrencia} onChange={e => handleHistoryChange(idx, 'ocorrencia', e.target.value)} disabled={row.semRegistro} className={`w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-1 focus:ring-emerald-500 outline-none placeholder:text-slate-300 dark:bg-slate-800 shadow-sm text-xs ${row.semRegistro ? 'opacity-50 cursor-not-allowed' : ''}`} placeholder="Ex: Renegociação" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Classification Result */}
                    <div className="bg-white dark:bg-slate-900/80 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm">
                        <div className="flex flex-col md:flex-row gap-6 items-center">
                            <div className="flex-1">
                                <h4 className="font-bold text-slate-800 dark:text-slate-200 mb-2">Classificação de Pagamento</h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 font-medium">Média de <span className="font-bold text-slate-700 dark:text-slate-200">{activeUcCalc.ucAvgDelay} dias</span> de atraso no histórico.</p>
                                <div className="space-y-1.5">
                                    {[
                                        { label: 'Excelente – paga antes ou no dia do vencimento', cls: 'Excelente' },
                                        { label: 'Bom – até 5 dias após vencimento', cls: 'Bom' },
                                        { label: 'Médio – Até 15 dias após vencimento', cls: 'Médio' },
                                        { label: 'Ruim – Até 30 dias após vencimento', cls: 'Ruim' },
                                        { label: 'Péssimo – Até 60 dias após o vencimento', cls: 'Péssimo' },
                                        { label: 'Calote – >60 dias ou renegociação, ligação à revelia, parcelamento', cls: 'Calote' },
                                    ].map(item => (
                                        <div key={item.cls} className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all duration-200 ${activeUcCalc.cls === item.cls ? 'bg-primary/8 dark:bg-primary/15 border-primary/30 dark:border-primary/20 shadow-sm' : 'bg-slate-50/50 dark:bg-slate-800/30 border-transparent opacity-50'}`}>
                                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${activeUcCalc.cls === item.cls ? 'border-primary bg-primary' : 'border-slate-300 dark:border-slate-600'}`}>
                                                {activeUcCalc.cls === item.cls && <div className="w-1.5 h-1.5 rounded-full bg-white"></div>}
                                            </div>
                                            <span className={`text-sm font-semibold ${activeUcCalc.cls === item.cls ? 'text-slate-800 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}`}>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="w-full md:w-64 flex flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-white dark:from-slate-800/40 dark:to-slate-900/40 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm text-center">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">Média de Atraso</span>
                                <span className={`text-5xl font-black my-3 tracking-tighter tabular-nums ${activeUcCalc.ucAvgDelay > 15 ? 'text-red-500' : activeUcCalc.ucAvgDelay > 0 ? 'text-amber-500' : 'text-primary'}`}>
                                    {activeUcCalc.ucAvgDelay}
                                </span>
                                <span className="text-sm font-bold text-slate-400">dias corridos</span>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex items-center justify-end gap-3 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-xl text-sm font-bold text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 transition-all duration-200 active:scale-[0.97]"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        className="group px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-primary to-primary/90 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98] transition-all duration-200 flex items-center gap-2"
                        title="Salvar progresso atual"
                    >
                        {activeUcCalc.isComplete ? 'Salvar e Validar UC' : 'Salvar Preenchimento Parcial'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
